import { useId } from 'react'
import { match, P } from 'ts-pattern'
import type { BeaconEffort, BeaconSummary, CoverageAlarm } from '../../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../../utils/text-utils.ts'
import { displayedVerdictLevel } from '../beacon-run.ts'
import * as styles from './beacon-coverage.css.ts'
import {
  type CoverageGap,
  coverageGaps,
  groupCoverageGaps,
  VERDICT_STATUS_LABELS,
  verdictHeadline,
} from './coverage-gaps.ts'
import type { CoverageModel } from './coverage-model.ts'

const GAUGE_RADIUS = 38
const GAUGE_CIRCUMFERENCE = 2 * Math.PI * GAUGE_RADIUS

const NO_GUARANTEE = 'This describes what was examined, not a guarantee that no weakness exists.'

/**
 * A run with no verified findings. Whether Beacon raised anything is read from its candidates, not from the
 * dismissed-lead list, which an older summary leaves empty.
 */
const zeroFindingsSentence = (summary: BeaconSummary) => {
  const raised = summary.coverage.proof.candidates_raw
  const listed = summary.dismissed.length
  const dismissed = Math.max(listed, summary.counts.false_positive)
  const weaknesses = (count: number) => `${count} suspected ${pluralize(count, 'weakness', 'weaknesses')}`
  return match({ raised, listed })
    .with(
      { raised: 0 },
      () => `No verified findings, and Beacon raised no suspected weakness to rule out. ${NO_GUARANTEE}`
    )
    .with(
      { listed: P.number.gt(0) },
      () =>
        `No verified findings. Beacon ruled out all ${weaknesses(dismissed)} it raised; the leads it dismissed are listed below with why. ${NO_GUARANTEE}`
    )
    .otherwise(
      () =>
        `No verified findings. Beacon raised ${weaknesses(raised)} and ruled out every one; this run’s summary does not list them. ${NO_GUARANTEE}`
    )
}

const GapCard: React.FC<{ gap: CoverageGap }> = ({ gap }) => {
  const titleId = useId()
  return (
    <li className={styles.gap({ severity: gap.severity })} aria-labelledby={titleId}>
      <p className={styles.gapTitle}>
        <span id={titleId}>{gap.title}</span>
        {gap.count !== undefined && (
          <span className={styles.gapCount} aria-label={`${gap.count} affected`}>
            {gap.count}
          </span>
        )}
      </p>
      {gap.items.length > 0 && (
        <ul className={styles.gapItems} aria-label="Affected">
          {gap.items.map(item => (
            <li key={item} className={styles.gapItem}>
              {item}
            </li>
          ))}
        </ul>
      )}
      {gap.sentences.map(sentence => (
        <p key={sentence} className={styles.gapSentence}>
          {sentence}
        </p>
      ))}
      {gap.nextStep && (
        <p className={styles.gapNextStep}>
          <b>Next step:</b> {gap.nextStep}
        </p>
      )}
    </li>
  )
}

const GapList: React.FC<{ gaps: readonly CoverageGap[] }> = ({ gaps }) =>
  gaps.length > 0 ? (
    <ul className={styles.gaps} aria-label="Gaps">
      {gaps.map(gap => (
        <GapCard key={gap.cause} gap={gap} />
      ))}
    </ul>
  ) : null

/** A run's gaps grouped by cause, each with what it affects and what, if anything, to do about it. */
export const BeaconCoverageGaps: React.FC<{ alarms: readonly CoverageAlarm[]; effort: BeaconEffort }> = ({
  alarms,
  effort,
}) => <GapList gaps={groupCoverageGaps(alarms, effort)} />

/**
 * How much of the attack surface Beacon examined, and for every gap why it was left and what to do next.
 * Every sentence is about what was examined; a run with no verified findings never claims nothing is there.
 */
export const BeaconCoverageStrip: React.FC<{ model: CoverageModel }> = ({ model }) => {
  const { coverage, counts, effort } = model.summary
  const { verdict } = coverage.proof
  const percent = coverage.examined_percent
  const level = displayedVerdictLevel(verdict, percent)
  const examined = (items: readonly { status: string }[]) => items.filter(item => item.status === 'examined').length
  const auditable = coverage.controls.filter(control => control.auditable)

  return (
    <section className={styles.strip} aria-label="Verdict">
      <div className={styles.verdictText}>
        <span className={styles.verdictStatus({ level })}>{VERDICT_STATUS_LABELS[level]}</span>
        <h2 className={styles.verdictTitle}>{verdictHeadline(verdict, percent)}</h2>
        <p className={styles.verdictSentence}>{verdict.sentence}</p>
        {counts.findings_kept === 0 && <p className={styles.verdictSentence}>{zeroFindingsSentence(model.summary)}</p>}
        <GapList gaps={coverageGaps(coverage, effort)} />
      </div>
      <div className={styles.gauge}>
        <svg
          viewBox="0 0 96 96"
          className={styles.gaugeSvg}
          role="img"
          aria-label={`${percent}% of the attack surface examined`}
        >
          <circle cx="48" cy="48" r={GAUGE_RADIUS} className={styles.gaugeTrack} />
          <circle
            cx="48"
            cy="48"
            r={GAUGE_RADIUS}
            className={styles.gaugeValue}
            strokeDasharray={`${(percent / 100) * GAUGE_CIRCUMFERENCE} ${GAUGE_CIRCUMFERENCE}`}
            transform="rotate(-90 48 48)"
          />
          <text className={styles.gaugeNumber} x="48" y="52" textAnchor="middle">
            {Math.round(percent)}%
          </text>
          <text className={styles.gaugeCaption} x="48" y="66" textAnchor="middle">
            EXAMINED
          </text>
        </svg>
        <div className={styles.gaugeLegend}>
          <span>
            <b>
              {examined(coverage.entry_points)}/{coverage.entry_points.length}
            </b>{' '}
            entry points
          </span>
          <span>
            <b>
              {examined(coverage.assets)}/{coverage.assets.length}
            </b>{' '}
            sensitive assets
          </span>
          <span>
            <b>
              {examined(auditable)}/{auditable.length}
            </b>{' '}
            checkable security controls
          </span>
          <span>
            <b>{counts.operations}</b> operations
          </span>
        </div>
      </div>
    </section>
  )
}
